import React, { useEffect } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { InputLabel, TextField } from '@material-ui/core';
import FormControl from 'global/react-mui/atoms/FormControl';
import { FormControlEnhancedProps } from 'global/react-mui/atoms/FormControl/FormControl';
import { INPUT_HEIGHT } from 'global/react-mui/theme/constants';
import { CustomTextField } from 'global/react-mui/atoms/InputField/styles';

type InputAutoCompleteProps = {
	options?: string[];
	ariaLabel?: string;
	label?: string;
	formControlProps?: FormControlEnhancedProps;
	handleOnChange: (value: string) => void;
	handleOnInputChange: (value: string) => void;
	placeholder?: string;
	id?: string;
	value: string;
	inputValue: string;
	disabled: boolean;
};

const InputAutoComplete: React.FC<InputAutoCompleteProps> = ({
	label,
	ariaLabel,
	options = [],
	value,
	inputValue,
	disabled,
	id,
	formControlProps,
	placeholder,
	handleOnChange,
	handleOnInputChange,
}) => {
	const classes = useStyles();

	const handleSafeSubmitOnChange = (_, value) => {
		if (options.includes(value)) handleOnChange(value as string);
	};

	useEffect(() => {
		if (inputValue !== value && inputValue === '') {
			handleOnChange(inputValue as string);
		} else {
			handleOnInputChange(inputValue);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inputValue]);

	return (
		<FormControl className={classes.root} {...formControlProps}>
			{label && (
				<InputLabel shrink htmlFor={id} className={classes.label}>
					{label}
				</InputLabel>
			)}
			<Autocomplete
				freeSolo
				disabled={disabled}
				options={options}
				classes={{
					option: classes.option,
					listbox: classes.listbox,
				}}
				autoHighlight
				inputValue={inputValue}
				getOptionLabel={(option) => option}
				onChange={handleSafeSubmitOnChange}
				onInputChange={(_, newInputValue) => {
					handleOnInputChange(newInputValue);
				}}
				renderOption={(option) => (
					<div className={classes.ellipsis} key={option}>
						{option}
					</div>
				)}
				renderInput={(params) => (
					<CustomTextField
						{...params}
						variant="outlined"
						placeholder={placeholder}
						className={classes.textfield}
						inputProps={{
							...params.inputProps,
							'aria-label': ariaLabel,
							autoComplete: 'new-password', // disable autocomplete and autofill
						}}
					/>
				)}
			/>
		</FormControl>
	);
};

const useStyles = makeStyles({
	ellipsis: {
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	option: {
		fontSize: 15,
		'& > span': {
			marginRight: 10,
			fontSize: 18,
		},
	},
	root: {
		display: 'flex',
		flexDirection: 'column',
		flexWrap: 'wrap',
		minHeight: 72,
		justifyContent: 'flex-end',
		marginLeft: 0,
		marginRight: 0,
	},
	textfield: {
		marginLeft: 0,
		marginRight: 0,
		'& div': {
			justifyContent: 'center',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			'& input': {
				minWidth: '100% !important',
			},
		},
	},
	label: {
		whiteSpace: 'nowrap',
	},
	listbox: {
		'& li': {
			height: INPUT_HEIGHT,
		},
	},
});

InputAutoComplete.defaultProps = {
	formControlProps: { minWidth: 280, marginLeft: 0, marginRight: 0 },
};

export default InputAutoComplete;
